import React, { useEffect, useRef } from "react";
import type { IAuthData } from "../../context/auth-provider";
import useAuth from "../../context/auth-provider";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { cleanupEvent, EventConstants, listenEvent } from "@/lib/event-emitter";
import { refreshAccessToken } from "@/services";

// @ts-expect-error -- untyped module
import Error403 from "@/assets/403-error.svg";
// @ts-expect-error -- untyped module
import Error401 from "@/assets/401-error.svg";

import ScreenRouteNames from "@/constants/screen-route-names";

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const auth = useAuth();
  const isRedirectingRef = useRef(false);
  // const path = location.pathname.split("/")[1];
  // console.log("Route Debug: ", { path, auth });

  const refreshToken = async () => {
    // console.log("Need to refresh token -----");
    const response = await refreshAccessToken(auth.authData!);
    if (response?.status === 200) {
      // console.log(response.data);
      const authData = { ...auth.authData } as IAuthData;
      authData.user!.access_token = response.data.access_token;
      authData.user!.refresh_token = response.data.refresh_token;
      auth.updateAuthData(authData);
    }
  };
  const sessionExpired = () => {
    // console.log("Session expired.");
    const redirectUrl = auth.authData?.user?.redirectUrl;
    isRedirectingRef.current = true;
    auth.logout();
    navigate(`/${ScreenRouteNames.SESSION_EXPIRED.path}`, {
      state: {
        path: location.pathname,
        redirectUrl,
      },
      replace: true,
    });
  };

  const unauthorized = () => {
    // console.log("Need to handle unauthorized access");
    isRedirectingRef.current = true;
    auth.logout();
    navigate("/error", {
      state: {
        path: location.pathname,
        error: {
          title: "401 Unauthorized",
          message:
            "You must be authenticated to view this page. Please log in and try again. If you believe this is an error, contact our support team.",
          statusCode: 401,
          image: Error401,
        },
      },
      replace: true,
    });
  };

  const logout = () => {
    isRedirectingRef.current = true;
    auth.logout();
    navigate(`/${ScreenRouteNames.LOGIN.path}`, { replace: true });
  };

  useEffect(() => {
    if (auth?.authData) {
      listenEvent(EventConstants.REFRESH_TOKEN, refreshToken);
      listenEvent(EventConstants.SESSION_EXPIRED, sessionExpired);
      listenEvent(EventConstants.LOGOUT, logout);
      listenEvent(EventConstants.UNAUTHORIZED, unauthorized);
      return () => {
        cleanupEvent();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!auth?.authData) {
    // A handler above is already navigating somewhere deliberate -- render
    // nothing rather than racing it with the access-denied redirect.
    if (isRedirectingRef.current) {
      return null;
    }
    return (
      <Navigate
        to="/error"
        state={{
          path: location.pathname,
          error: {
            title: "Access Denied",
            message:
              "You don't have permission to view this page. If you believe this is an error, please contact our support team.",
            statusCode: 403,
            image: Error403,
          },
        }}
        replace={true}
      />
    );
  } else {
    return children;
  }
};

export default RequireAuth;
